/*
 * The in-place edit of one message, attachments included (spec 07 §4, [14 §3.7]).
 *
 * It exists as its own component because of the attachments and nothing else: the edit box is the
 * ordinary composer, seeded with the message's markdown *and* with the files it already carries, so
 * that the set the composer hands back on send is the complete new set — which is exactly what
 * `chat.message.edit` wants. An edit that forgot to send them would unbind every file the message
 * has, because the server takes `attachments` as the whole truth (protocol.md §2.6).
 *
 * The send goes through `ChatDeps.edit`, which the boot binds to the outbox: removing a file is
 * therefore as durable as sending one — the change is on disk before the socket sees it and it
 * survives a reload halfway through — and this component knows nothing about how an edit travels.
 */

import { useCallback, useMemo } from "react"
import { Composer } from "../../../core/composer/Composer.tsx"
import type { AutocompleteSource, Render } from "../../../core/composer/types.ts"
import type { AttachmentRef } from "../../../core/drafts/types.ts"
import { EmojiPicker } from "../../../core/emoji/EmojiPicker.tsx"
import { MarkdownPreview } from "../../../core/render/MarkdownBody.tsx"
import type { AttachmentWire } from "../../../core/wire/index.ts"
import { chatDeps } from "../deps.tsx"
import type { MessageView } from "../wire/views.ts"

/** The attachments of a sent message as the composer's tray takes them. */
export function refsOf(attachments: readonly AttachmentWire[]): AttachmentRef[] {
	return attachments.map((file) => {
		const ref: AttachmentRef = {
			id: file.id,
			name: file.filename,
			bytes: file.bytes,
			contentType: file.mime,
			kind: file.kind,
			thumbUrl: file.thumbUrl,
		}
		if (file.caption !== "") ref.caption = file.caption
		return ref
	})
}

export interface MessageEditorProps {
	msg: MessageView
	sources: AutocompleteSource[]
	preview: (markdown: string) => Promise<Render>
	/** Closes the box — after a send, on Escape and on Cancel alike. */
	onClose: () => void
}

export function MessageEditor({ msg, sources, preview, onClose }: MessageEditorProps) {
	const deps = chatDeps()
	const initialAttachments = useMemo(() => refsOf(msg.attachments ?? []), [msg.attachments])
	// The seam is taken here, where it can still be answered with "then there is no edit box".
	// Optional at the call would mean an unbound edit resolves to `undefined`, the core reads that
	// as an accepted send, and the box clears its draft and closes: the edit would be gone without a
	// word (`deps.tsx` — an action whose call is not bound is not offered).
	const edit = deps.edit

	const send = useCallback(
		async (markdown: string, attachments: AttachmentRef[]) => {
			if (edit === undefined) return
			await edit({ id: msg.id, markdown, attachments, baseVersion: msg.version })
			onClose()
		},
		[edit, msg.id, msg.version, onClose],
	)

	if (edit === undefined) return null

	return (
		<Composer
			targetKey={`e:${msg.id}`}
			placeholderKey="chat.composer.editPlaceholder"
			sources={sources}
			initialMarkdown={msg.markdown ?? ""}
			initialAttachments={initialAttachments}
			onSend={send}
			onCancel={onClose}
			preview={preview}
			renderPreview={MarkdownPreview}
			// The box opens with the caret in it and with the compact frame the row has space for;
			// the format row is behind the fold's toggle, as in the thread pane (EDITOR §7.1, §13.2).
			// eslint-disable-next-line jsx-a11y/no-autofocus
			autoFocus
			compact
			toolbar="overflow"
			renderEmojiPicker={EmojiPicker}
		/>
	)
}
